import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  Briefcase,
  Building2,
  Headphones,
  Search,
  User,
  Users,
  Wallet,
} from "lucide-react";
import {
  MOCK_CASES,
  MOCK_CUSTOMERS,
  MOCK_INSTITUTIONS,
  MOCK_PAYMENTS,
} from "../../data/mock/dashboardData";
import { USE_MOCK_AUTH } from "../../config/api";
import { getInstitutions, subscribeInstitutions } from "../../data/institutionsStore";
import { listOpsUsers } from "../../data/opsUsersStore";
import { opsCasesPath } from "../../utils/opsCasesPath";

const TYPE_META = {
  case: { label: "Case", icon: Briefcase, tone: "bg-primary/[0.08] text-primary" },
  customer: { label: "Customer", icon: Users, tone: "bg-sky-50 text-sky-700" },
  institution: { label: "Institution", icon: Building2, tone: "bg-amber-50 text-amber-800" },
  agent: { label: "Team", icon: User, tone: "bg-emerald-50 text-emerald-700" },
  payment: { label: "Payment", icon: Wallet, tone: "bg-violet-50 text-violet-700" },
  page: { label: "Page", icon: Headphones, tone: "bg-primary/[0.06] text-primary/70" },
};

function buildIndex() {
  const pages = [
    { id: "page-cases", type: "case", title: "Collection Cases", subtitle: "Ops caseload", to: "/dashboard/cases" },
    { id: "page-customers", type: "page", title: "Customers", subtitle: "Debtor profiles", to: "/dashboard/customers" },
    { id: "page-payments", type: "page", title: "Payments", subtitle: "Collections ledger", to: "/dashboard/payments" },
    { id: "page-institutions", type: "page", title: "Institutions", subtitle: "Service providers", to: "/dashboard/institutions" },
    { id: "page-team", type: "page", title: "Team", subtitle: "Ops team members", to: "/dashboard/team" },
    { id: "page-roles", type: "page", title: "Roles & permissions", subtitle: "Access control", to: "/dashboard/roles" },
    { id: "page-contact", type: "page", title: "Contact Centre", subtitle: "Calls and plans", to: "/dashboard/contact-centre" },
  ];

  const cases = USE_MOCK_AUTH
    ? MOCK_CASES.map((row) => ({
        id: `case-${row.id}`,
        type: "case",
        title: row.id,
        subtitle: `${row.customer} · ${row.institution}`,
        to: opsCasesPath({
          account: row.accountNumber || row.account_number,
          caseId: row.id || row.caseId,
        }),
        searchText: `${row.id} ${row.customer} ${row.institution} ${row.assignedAgent ?? ""}`,
      }))
    : [];

  const customers = USE_MOCK_AUTH
    ? MOCK_CUSTOMERS.map((row) => ({
        id: `customer-${row.id}`,
        type: "customer",
        title: row.name,
        subtitle: row.phone,
        to: `/dashboard/customers?customer=${encodeURIComponent(row.name)}`,
        searchText: `${row.name} ${row.phone} ${row.accountNumber} ${row.id}`,
      }))
    : [];

  const institutionRows = USE_MOCK_AUTH ? MOCK_INSTITUTIONS : getInstitutions();
  const institutions = institutionRows.map((row) => ({
    id: `ins-${row.id}`,
    type: "institution",
    title: row.name || row.legal_name,
    subtitle: `${row.code ?? row.regNumber ?? row.id} · ${row.region ?? ""}`,
    to: `/dashboard/institutions/${row.id}`,
    searchText: `${row.name || row.legal_name || ""} ${row.code ?? ""} ${row.region ?? ""}`,
  }));

  const agents = listOpsUsers().map((row) => ({
    id: `agent-${row.id}`,
    type: "agent",
    title: row.name,
    subtitle: `${row.email} · ${row.role}`,
    to: "/dashboard/team",
    searchText: `${row.name} ${row.email} ${row.role}`,
  }));

  const payments = USE_MOCK_AUTH
    ? MOCK_PAYMENTS.slice(0, 30).map((row) => ({
        id: `pay-${row.id}`,
        type: "payment",
        title: row.id,
        subtitle: `${row.customer} · ${row.caseId}`,
        to: `/dashboard/payments?customer=${encodeURIComponent(row.customer)}`,
        searchText: `${row.id} ${row.customer} ${row.caseId} ${row.reference}`,
      }))
    : [];

  return [...pages, ...cases, ...customers, ...institutions, ...agents, ...payments];
}

export default function GlobalSearch({
  placeholder = "Search cases, customers, agents…",
  className = "",
  mobile = false,
}) {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(!mobile);
  const [institutionTick, setInstitutionTick] = useState(0);

  useEffect(() => {
    if (USE_MOCK_AUTH) return undefined;
    return subscribeInstitutions(() => setInstitutionTick((n) => n + 1));
  }, []);

  const index = useMemo(() => buildIndex(), [institutionTick]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return index
      .filter((item) => (item.searchText ?? `${item.title} ${item.subtitle}`).toLowerCase().includes(q))
      .slice(0, 8);
  }, [index, query]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) {
        setOpen(false);
        if (mobile) setExpanded(false);
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        if (mobile) setExpanded(false);
      }
    };
    document.addEventListener("pointerdown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, mobile]);

  const go = (item) => {
    setQuery("");
    setOpen(false);
    if (mobile) setExpanded(false);
    navigate(item.to);
  };

  if (mobile && !expanded) {
    return (
      <button
        type="button"
        className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-primary/50 transition-colors hover:bg-primary/[0.04] hover:text-primary"
        aria-label="Search"
        onClick={() => {
          setExpanded(true);
          setOpen(true);
        }}
      >
        <Search className="h-4 w-4" />
      </button>
    );
  }

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <label className="relative block w-full">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary/30" />
        <input
          type="search"
          value={query}
          autoFocus={mobile}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="w-full rounded-lg border border-primary/10 bg-primary/[0.02] py-2 pl-9 pr-3 text-sm text-primary placeholder:text-primary/30 transition-colors focus:border-accent/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-accent/20"
        />
      </label>

      {open && query.trim().length >= 2 && (
        <div className="absolute left-0 right-0 top-[calc(100%+6px)] z-50 max-h-80 overflow-auto rounded-xl border border-primary/10 bg-white p-1.5 shadow-xl shadow-primary/10">
          {results.length === 0 ? (
            <p className="px-3 py-4 text-center text-sm text-primary/45">No matches for “{query.trim()}”</p>
          ) : (
            <ul>
              {results.map((item) => {
                const meta = TYPE_META[item.type] ?? TYPE_META.page;
                const Icon = meta.icon;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => go(item)}
                      className="flex w-full items-start gap-3 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-primary/[0.04]"
                    >
                      <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${meta.tone}`}>
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-semibold text-primary">{item.title}</span>
                          <span className="rounded bg-primary/[0.05] px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary/45">
                            {meta.label}
                          </span>
                        </span>
                        <span className="mt-0.5 block truncate text-xs text-primary/45">{item.subtitle}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
